global.Buffer = global.Buffer || require('buffer').Buffer;

import React, {useEffect, useState} from 'react';

import {
  CardImage,
  Container,
  Content,
  StoresContainer,
  Hero,
  HeroHeader,
  HeroTitle,
  Badge,
  BadgeText,
  Form,
  FormSurface,
  Title,
  StoresList,
  CategorySection,
  StoreScrollContainer,
  StoresColumn,
  CategoryHeader,
  StoreContainer,
  CardContainer,
  CardSurface,
  CardTopRow,
  CardTitle,
  CardBadge,
  CardBadgeText,
  ButtonSearch,
  CardImageContainer,
  StoresColumnList,
  SectionDescription,
} from './styles';
import {Header} from '../../components/Interface/Headers/Header';
import {apiMasterClin} from '../../services/api';
import {getCache, setCache, CACHE_KEYS} from '../../services/cache';
import {SearchInput} from '../../components/Interface/Forms/Inputs/SearchInput';
import {LinkButton} from '../../components/Interface/Forms/Buttons/LinkButton';
import {useIsFocused, useNavigation, useRoute} from '@react-navigation/native';
import {ParceiroDTO, parceiroCategorias} from '../../dtos/ParceiroDTO';
import {RFValue} from 'react-native-responsive-fontsize';
import {StoreItem} from '../../components/shared/Stores/StoreItem';
import {Titles} from '../../components/Interface/Typographies/Titles';
import {ClubeCardShimmer} from '../../components/Interface/Loadings/ClubeShimmer/ClubeCardShimmer';
import {HorizontalStoreShimmer} from '../../components/Interface/Loadings/ClubeShimmer/HorizontalStoreShimmer';
import {CategoryStoreShimmer} from '../../components/Interface/Loadings/ClubeShimmer/CategoryStoreShimmer';
import {BackHandler, Dimensions} from 'react-native';

interface Params {
  id_masterclin: string;
}

export function Clube() {
  const navigation = useNavigation();
  const router = useRoute();

  const [isLoading, setIsLoading] = useState(true);

  const {id_masterclin: id} = router.params as Params;

  const [card, setCard] = useState('');
  const [parceiros, setParceiros] = useState<ParceiroDTO[]>(
    [] as ParceiroDTO[],
  );
  const [lojasDestaque, setLojasDestaque] = useState<ParceiroDTO[]>(
    [] as ParceiroDTO[],
  );

  const isFocused = useIsFocused();

  useEffect(() => {
    let isMounted = true;

    async function fetchListaLojasDestaque() {
      // Tentar buscar do cache primeiro
      const cached = await getCache<ParceiroDTO[]>(CACHE_KEYS.MASTERCLIN_DESTAQUES);
      if (cached && isMounted) {
        setLojasDestaque(cached);
        setIsLoading(false);
        return;
      }

      await apiMasterClin
        .get('/parceiro/obter-destaques')
        .then(({data}) => {
          const {parceiros} = data;

          if (isMounted) {
            setLojasDestaque(parceiros);
            // Salvar no cache por 7 dias
            setCache(CACHE_KEYS.MASTERCLIN_DESTAQUES, parceiros);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    async function fetchListaLojas() {
      await apiMasterClin.get('/parceiro').then(({data}) => {
        const {dados} = data;

        if (isMounted) {
          setParceiros(dados);
          fetchListaLojasDestaque();
        }
      });
    }

    async function fetchCartaoBeneficiario() {
      await apiMasterClin
        .get(`/beneficiarios/${id}/emitir-cartao`, {
          responseType: 'arraybuffer',
        })
        .then(response => {
          const base64Image = Buffer.from(response.data, 'binary').toString(
            'base64',
          );

          setCard(base64Image);
          fetchListaLojas();
        });
    }

    fetchCartaoBeneficiario();

    return () => {
      isMounted = false;
    };
  }, [isFocused]);

  function listByCategory(categoria: string) {
    const filtraParceiros = parceiros.filter(
      parceiro => parceiro.categoria[0].nome === categoria,
    );

    if (parceiros.length !== 0) {
      return filtraParceiros;
    }
    return parceiros;
  }

  useEffect(() => {
    BackHandler.addEventListener('hardwareBackPress', function () {
      navigation.navigate('Home');
      return true;
    });
  }, []);

  const [size, setSize] = useState({
    width: Dimensions.get('window').width - 24,
    height: 0,
  });

  return (
    <Container>
      <Header
        icon="chevron-left"
        action={() => navigation.navigate('Initial')}
      />

      <Content
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{paddingBottom: RFValue(20)}}>
        <Hero>
          <HeroHeader>
            <HeroTitle>
              <Titles
                title="Meu clube"
                subtitle="Confira todas as vantagens que você possui por ser um cliente Baratão."
              />
            </HeroTitle>
            <Badge>
              <BadgeText>Clube ativo</BadgeText>
            </Badge>
          </HeroHeader>

          <CardContainer>
            <CardSurface>
              <CardTopRow>
                <CardTitle>Seu cartão digital</CardTitle>
                <CardBadge>
                  <CardBadgeText>Apresente na loja</CardBadgeText>
                </CardBadge>
              </CardTopRow>
              <CardImageContainer>
                {isLoading ? (
                  <ClubeCardShimmer />
                ) : (
                  <CardImage
                    source={{uri: `data:image/jpeg;base64,${card}`}}
                    resizeMode="stretch"
                  />
                )}
              </CardImageContainer>
            </CardSurface>
          </CardContainer>
        </Hero>

        <StoresContainer>
          <FormSurface>
            <Title>Encontre parceiros</Title>
            <Form>
              <ButtonSearch
                activeOpacity={1}
                onPress={() => navigation.navigate('StoreSearch', {})}>
                <SearchInput
                  isButtonInput={{
                    action: () => navigation.navigate('StoreSearch', {}),
                    text: 'Busque por lojas, restaurantes e muito mais',
                  }}
                />
              </ButtonSearch>
            </Form>
          </FormSurface>

          <CategorySection
            onLayout={event => {
              const {width, height} = event.nativeEvent.layout;
              setSize({width: width - RFValue(24), height});
            }}>
            <CategoryHeader>
              <Title>Lojas em destaque</Title>

              <LinkButton
                text="Ver tudo"
                action={() =>
                  navigation.navigate('StoreSearch', {destaques: true})
                }
                enabled
              />
            </CategoryHeader>
            <SectionDescription>
              Parceiros com as melhores vantagens ativas no momento.
            </SectionDescription>

            <StoreScrollContainer
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{}}>
              {!isLoading ? (
                <StoresColumnList>
                  {lojasDestaque.slice(0, 3).length === 3 && (
                    <StoresColumn>
                      {lojasDestaque.slice(0, 3).map((parceiro, index) => (
                        <StoreItem
                          key={parceiro.id}
                          data={parceiro}
                          horizontal
                          first={index === 0}
                        />
                      ))}
                    </StoresColumn>
                  )}

                  {lojasDestaque.slice(3, 6).length === 3 && (
                    <StoresColumn>
                      {lojasDestaque.slice(3, 6).map((parceiro, index) => (
                        <StoreItem
                          key={parceiro.id}
                          data={parceiro}
                          horizontal
                          first={index === 0}
                        />
                      ))}
                    </StoresColumn>
                  )}

                  {lojasDestaque.slice(6, 9).length === 3 && (
                    <StoresColumn>
                      {lojasDestaque.slice(6, 9).map((parceiro, index) => (
                        <StoreItem
                          key={parceiro.id}
                          data={parceiro}
                          horizontal
                          first={index === 0}
                        />
                      ))}
                    </StoresColumn>
                  )}
                </StoresColumnList>
              ) : (
                <HorizontalStoreShimmer />
              )}
            </StoreScrollContainer>
          </CategorySection>

          {!isLoading ? (
            <StoresList>
              {parceiroCategorias.map(
                categoria =>
                  listByCategory(categoria).length >= 3 && (
                    <CategorySection key={categoria}>
                      <CategoryHeader>
                        <Title>{categoria}</Title>

                        <LinkButton
                          text="Ver tudo"
                          action={() =>
                            navigation.navigate('StoreSearch', {categoria})
                          }
                          enabled
                        />
                      </CategoryHeader>
                      {listByCategory(categoria).length >= 6 ? (
                        <StoreScrollContainer
                          horizontal
                          showsHorizontalScrollIndicator={false}>
                          <StoresColumnList>
                            {listByCategory(categoria).slice(0, 3).length ===
                              3 && (
                              <StoresColumn>
                                {listByCategory(categoria)
                                  .slice(0, 3)
                                  .map((parceiro, index) => (
                                    <StoreItem
                                      key={parceiro.id}
                                      data={parceiro}
                                      horizontal
                                      first={index === 0}
                                    />
                                  ))}
                              </StoresColumn>
                            )}

                            {listByCategory(categoria).slice(3, 6).length ===
                              3 && (
                              <StoresColumn>
                                {listByCategory(categoria)
                                  .slice(3, 6)
                                  .map((parceiro, index) => (
                                    <StoreItem
                                      key={parceiro.id}
                                      data={parceiro}
                                      horizontal
                                      first={index === 0}
                                    />
                                  ))}
                              </StoresColumn>
                            )}

                            {listByCategory(categoria).slice(6, 9).length ===
                              3 && (
                              <StoresColumn>
                                {listByCategory(categoria)
                                  .slice(6, 9)
                                  .map((parceiro, index) => (
                                    <StoreItem
                                      key={parceiro.id}
                                      data={parceiro}
                                      horizontal
                                      first={index === 0}
                                    />
                                  ))}
                              </StoresColumn>
                            )}
                          </StoresColumnList>
                        </StoreScrollContainer>
                      ) : (
                        <StoreContainer>
                          {listByCategory(categoria).slice(0, 3).length !== 0 &&
                            listByCategory(categoria)
                              .slice(0, 3)
                              .map((parceiro, index) => (
                                <StoreItem
                                  key={parceiro.id}
                                  data={parceiro}
                                  first={index === 0}
                                />
                              ))}
                        </StoreContainer>
                      )}
                    </CategorySection>
                  ),
              )}
            </StoresList>
          ) : (
            <StoresList style={{marginTop: RFValue(12)}}>
              <CategoryStoreShimmer />
            </StoresList>
          )}
        </StoresContainer>
      </Content>
    </Container>
  );
}
